import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { bugReportService, type BugReport } from '../services/bugReportService'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const AdminBugReportsPage = () => {
  const navigate = useNavigate()
  const [bugReports, setBugReports] = useState<BugReport[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null)
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved' | 'closed'>('all')

  useEffect(() => {
    loadBugReports()
  }, [])

  const loadBugReports = async () => {
    try {
      setLoading(true)
      const reports = await bugReportService.getBugReports()
      setBugReports(reports)
    } catch (error) {
      console.error('Error loading bug reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateReportStatus = async (id: string, status: BugReport['status']) => {
    try {
      await bugReportService.updateBugReportStatus(id, status)
      await loadBugReports()
      if (selectedReport?.id === id) {
        setSelectedReport(prev => prev ? { ...prev, status } : null)
      }
    } catch (error) {
      console.error('Error updating bug report status:', error)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20'
      case 'high': return 'text-orange-400 bg-orange-500/20'
      case 'medium': return 'text-yellow-400 bg-yellow-500/20'
      case 'low': return 'text-green-400 bg-green-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-blue-400 bg-blue-500/20'
      case 'in_progress': return 'text-yellow-400 bg-yellow-500/20'
      case 'resolved': return 'text-green-400 bg-green-500/20'
      case 'closed': return 'text-gray-400 bg-gray-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return ClockIcon
      case 'in_progress': return ArrowPathIcon
      case 'resolved': return CheckCircleIcon
      case 'closed': return XMarkIcon
      default: return ClockIcon
    }
  }

  const filteredReports = bugReports.filter(report => 
    filter === 'all' || report.status === filter
  )

  const statusCounts = {
    all: bugReports.length,
    open: bugReports.filter(r => r.status === 'open').length,
    in_progress: bugReports.filter(r => r.status === 'in_progress').length,
    resolved: bugReports.filter(r => r.status === 'resolved').length,
    closed: bugReports.filter(r => r.status === 'closed').length
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Bug Reports Admin" subtitle="Manage user-submitted bug reports">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
        >
          {Object.entries(statusCounts).map(([status, count]) => (
            <motion.button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`glass-mockup rounded-xl p-4 text-center transition-all ${
                filter === status ? 'ring-2 ring-blue-500' : ''
              }`}
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-2xl font-bold text-white mb-1">{count}</div>
              <div className="text-gray-400 text-sm capitalize">
                {status === 'all' ? 'Total' : status.replace('_', ' ')}
              </div>
            </motion.button>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reports List */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-mockup rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Bug Reports ({filteredReports.length})
                </h2>
                <button
                  onClick={loadBugReports}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-400">Loading bug reports...</p>
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No bug reports found</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {filteredReports.map((report) => {
                    const StatusIcon = getStatusIcon(report.status || 'open')
                    return (
                      <motion.div
                        key={report.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`p-4 rounded-xl border transition-all cursor-pointer ${
                          selectedReport?.id === report.id
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                        }`}
                        onClick={() => setSelectedReport(report)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-white font-semibold mb-1">{report.title}</h3>
                            <p className="text-gray-400 text-sm">
                              by {report.userName} ({report.userEmail})
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(report.severity)}`}>
                              {report.severity}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status || 'open')}`}>
                              {report.status || 'open'}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                          {report.description}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center">
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {report.category}
                          </span>
                          <span>
                            {new Date(report.createdAt || '').toLocaleDateString()}
                          </span>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Report Details */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-mockup rounded-2xl p-6 sticky top-8"
            >
              {selectedReport ? (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-white">Report Details</h3>
                    <button
                      onClick={() => setSelectedReport(null)}
                      className="p-1 text-gray-400 hover:text-white transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-white font-semibold mb-2">{selectedReport.title}</h4>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedReport.severity)}`}>
                          {selectedReport.severity}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedReport.status || 'open')}`}>
                          {selectedReport.status || 'open'}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h5 className="text-gray-400 text-sm font-medium mb-2">Description</h5>
                      <p className="text-gray-300 text-sm">{selectedReport.description}</p>
                    </div>

                    {selectedReport.steps && (
                      <div>
                        <h5 className="text-gray-400 text-sm font-medium mb-2">Steps to Reproduce</h5>
                        <p className="text-gray-300 text-sm whitespace-pre-line">{selectedReport.steps}</p>
                      </div>
                    )}

                    {selectedReport.expectedBehavior && (
                      <div>
                        <h5 className="text-gray-400 text-sm font-medium mb-2">Expected Behavior</h5>
                        <p className="text-gray-300 text-sm">{selectedReport.expectedBehavior}</p>
                      </div>
                    )}

                    {selectedReport.actualBehavior && (
                      <div>
                        <h5 className="text-gray-400 text-sm font-medium mb-2">Actual Behavior</h5>
                        <p className="text-gray-300 text-sm">{selectedReport.actualBehavior}</p>
                      </div>
                    )}

                    <div>
                      <h5 className="text-gray-400 text-sm font-medium mb-2">Technical Info</h5>
                      <div className="space-y-1 text-xs text-gray-500">
                        <div><strong>User:</strong> {selectedReport.userName} ({selectedReport.userEmail})</div>
                        <div><strong>URL:</strong> {selectedReport.url}</div>
                        <div><strong>Browser:</strong> {selectedReport.browserInfo}</div>
                        <div><strong>Submitted:</strong> {new Date(selectedReport.createdAt || '').toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Status Actions */}
                    <div>
                      <h5 className="text-gray-400 text-sm font-medium mb-2">Update Status</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                          <button
                            key={status}
                            onClick={() => updateReportStatus(selectedReport.id!, status as any)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              selectedReport.status === status
                                ? 'bg-blue-500 text-white'
                                : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                            }`}
                          >
                            {status.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <EyeIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Select a bug report to view details</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminBugReportsPage 